// src/registerSW.js
// Neo application build/update coordinator.
//
// BUILD 32 retains the permanent PWA update model:
// - the running app polls a tiny network-only build marker,
// - a new deployment is detected without closing the PWA,
// - safe kiosk/clock-in screens automatically refresh,
// - other screens receive an update-available event,
// - the Clock In refresh button can always perform a true hard app refresh.

export const NEO_BUILD_ID = "32";

const BUILD_MARKER_URL = "/neo-build.json";
const BUILD_KEY = "neo.activeBuildId";
const CHECK_INTERVAL_MS = 60 * 1000;

let monitorTimer = null;
let updateInFlight = false;
let lastRemoteBuild = "";

function normalizeBuild(value) {
  return String(value || "").replace(/^BUILD[-\s]*/i, "").trim();
}

export async function clearNeoRuntimeCaches() {
  try {
    if ("caches" in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch (error) {
    console.warn("[Neo Update] CacheStorage cleanup skipped", error);
  }

  // Ask any registered worker to update, but do not require users to close the PWA.
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map(async (registration) => {
          try {
            await registration.update();
          } catch {}
        })
      );
    }
  } catch (error) {
    console.warn("[Neo Update] service-worker update check skipped", error);
  }
}

export async function fetchLatestNeoBuild() {
  try {
    const response = await fetch(
      `${BUILD_MARKER_URL}?ts=${Date.now()}`,
      {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache, no-store, max-age=0",
          Pragma: "no-cache",
        },
      }
    );

    if (!response.ok) return "";
    const payload = await response.json();
    return normalizeBuild(payload?.build);
  } catch {
    return "";
  }
}

export function isNeoUpdateAvailable(remoteBuild) {
  const remote = normalizeBuild(remoteBuild);
  const current = normalizeBuild(NEO_BUILD_ID);
  return Boolean(remote && current && remote !== current);
}

function notifyUpdateAvailable(remoteBuild) {
  const build = normalizeBuild(remoteBuild);
  lastRemoteBuild = build;
  try {
    window.dispatchEvent(
      new CustomEvent("neo:update-available", {
        detail: {
          currentBuild: normalizeBuild(NEO_BUILD_ID),
          latestBuild: build,
        },
      })
    );
  } catch {}
}

function isSafeAutoRefreshRoute() {
  const path = String(window.location.pathname || "/").toLowerCase();
  return (
    path === "/" ||
    path === "/clock-in" ||
    path === "/clockin" ||
    path === "/staff-login"
  );
}

export async function forceNeoAppRefresh(remoteBuild = "") {
  if (updateInFlight) return;
  updateInFlight = true;

  const targetBuild = normalizeBuild(remoteBuild || lastRemoteBuild || NEO_BUILD_ID);

  try {
    await clearNeoRuntimeCaches();
  } catch {}

  try {
    localStorage.setItem(BUILD_KEY, targetBuild || normalizeBuild(NEO_BUILD_ID));
  } catch {}

  // A unique navigation URL forces Safari/PWA to request the current app shell
  // just like a browser refresh, without destroying the durable Neo device link.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("neo_build", targetBuild || normalizeBuild(NEO_BUILD_ID));
    url.searchParams.set("neo_refresh", String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

export async function checkForNeoUpdate({ autoRefreshSafeRoute = true } = {}) {
  const remoteBuild = await fetchLatestNeoBuild();
  if (!remoteBuild || !isNeoUpdateAvailable(remoteBuild)) {
    return { available: false, latestBuild: remoteBuild || normalizeBuild(NEO_BUILD_ID) };
  }

  notifyUpdateAvailable(remoteBuild);

  // Clock-in/home is the normal unattended kiosk position. It is safe to
  // self-refresh there so a fleet of stores does not need manual intervention.
  if (
    autoRefreshSafeRoute &&
    document.visibilityState === "visible" &&
    isSafeAutoRefreshRoute()
  ) {
    window.setTimeout(() => {
      forceNeoAppRefresh(remoteBuild);
    }, 2500);
  }

  return { available: true, latestBuild: remoteBuild };
}

function startUpdateMonitor() {
  if (monitorTimer) return;

  const run = () => {
    checkForNeoUpdate({ autoRefreshSafeRoute: true }).catch(() => {});
  };

  // Check shortly after startup, then continuously.
  window.setTimeout(run, 2500);
  monitorTimer = window.setInterval(run, CHECK_INTERVAL_MS);

  window.addEventListener("focus", run);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") run();
  });
}

export default function registerSW() {
  try {
    localStorage.setItem(BUILD_KEY, normalizeBuild(NEO_BUILD_ID));
  } catch {}

  if (document.readyState === "complete") {
    startUpdateMonitor();
  } else {
    window.addEventListener("load", startUpdateMonitor, { once: true });
  }
}
